import { SubscriptionPlan, TokenPackage, PlanFeature } from '@/types/subscription-types';
import { RawPlanData } from './plan-data-types';

/**
 * Gets the monthly token allocation for a given plan (fallback values)
 */
export const getMonthlyTokenAllocation = (planName: string): number => {
  const monthlyTokenAllocations = {
    "freemium": 10,
    "starter": 80,
    "professional": 600,
    "power user": 1200
  };
  
  return monthlyTokenAllocations[planName.toLowerCase()] || 10;
};

/**
 * Safely parses features data from the database into a structured PlanFeature array
 */
export const parseFeatures = (featuresData: any): PlanFeature[] => {
  const features: PlanFeature[] = [];
  
  try {
    // If it's a string, parse it
    const parsedFeatures = typeof featuresData === 'string'
      ? JSON.parse(featuresData)
      : featuresData;
    
    // Make sure it's an array
    if (Array.isArray(parsedFeatures)) {
      parsedFeatures.forEach(feature => {
        // Ensure we have a valid feature object with all required properties
        features.push({
          name: typeof feature.name === 'string' ? feature.name : '',
          included: typeof feature.included === 'boolean' ? feature.included : true,
          highlight: typeof feature.highlight === 'boolean' ? feature.highlight : false,
          bold: typeof feature.bold === 'boolean' ? feature.bold : false,
          icon: typeof feature.icon === 'string' ? feature.icon : undefined
        });
      });
    }
  } catch (err) {
    console.error('Error parsing features:', err);
  }
  
  return features;
};

/**
 * Safely parses price data from the database
 */
export const parsePrice = (priceData: any): number => {
  try {
    const parsed = typeof priceData === 'string' 
      ? JSON.parse(priceData) 
      : priceData;
    
    return parsed?.usd || 0;
  } catch (err) {
    console.error('Error parsing price:', err);
    return 0;
  }
};

/**
 * Creates default subscription plans when no data is available
 * These match the database structure and dropdown plans
 */
export const getDefaultPlans = (): SubscriptionPlan[] => {
  return [
    {
      id: 'freemium',
      name: 'Freemium',
      description: 'Perfect for getting started',
      price: 0,
      priceId: '',
      features: [
        { name: 'Basic AI Features', included: true },
        { name: '10 Credits per month', included: true },
        { name: 'Community Support', included: true }
      ],
      aiTokens: 10,
      billingPeriod: 'month' as const
    },
    {
      id: 'starter',
      name: 'Starter',
      description: 'Great for small teams',
      price: 17,
      priceId: 'starter-price-id',
      features: [
        { name: 'Advanced AI Features', included: true },
        { name: '9,000 Credits per month', included: true },
        { name: 'Priority Support', included: true },
        { name: 'Advanced Analytics', included: true }
      ],
      aiTokens: 9000,
      billingPeriod: 'month' as const
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'Perfect for growing businesses',
      price: 25,
      priceId: 'professional-price-id',
      features: [
        { name: 'Premium AI Features', included: true },
        { name: '19,000 Credits per month', included: true },
        { name: 'Premium Support', included: true },
        { name: 'Advanced Analytics', included: true },
        { name: 'Custom Integrations', included: true }
      ],
      aiTokens: 19000,
      billingPeriod: 'month' as const
    },
    {
      id: 'power-user',
      name: 'Power User',
      description: 'For power users and enterprises',
      price: 33,
      priceId: 'power-user-price-id',
      features: [
        { name: 'Enterprise AI Features', included: true },
        { name: '29,000 Credits per month', included: true },
        { name: 'Dedicated Support', included: true },
        { name: 'Enterprise Analytics', included: true },
        { name: 'Custom Integrations', included: true },
        { name: 'Priority Processing', included: true }
      ],
      aiTokens: 29000,
      billingPeriod: 'month' as const
    }
  ];
};

/**
 * Creates default token packages when no data is available
 */
export const getDefaultPackages = (): TokenPackage[] => {
  return [
    {
      id: 'token-package',
      name: 'Token Package',
      description: 'Additional AI tokens for your projects',
      price: 29,
      priceId: '',
      tokens: 10000,
      billingPeriod: 'month' as const
    }
  ];
};

/**
 * Legacy transformation functions - kept for potential future use
 * Currently using inline transformation in usePlanData hook
 */
